import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { Textfit } from "react-textfit";

import { get, cloneDeep } from "lodash";
import FaSortAlphaAsc from "react-icons/lib/fa/sort-alpha-asc";
import FaSortAlphaDesc from "react-icons/lib/fa/sort-alpha-desc";

import Loading from "../../blocks/Loading";
import ReportContainer from "../ReportContainer";
import DraggableStudentRow from "./DraggableStudentRow";
import GradeHeader from "../GradeHeader";
import { domainInfo } from "../../util/domainInfo";
import getSkillAreaWidth from "../../util/getSkillAreaWidth";
import SRS from "../../../../services/StudentRecordService";
import getPeriods from "../../util/getPeriods";

import SkillModal from '../SkillModal'

import "./index.scss";

const allPeriods = getPeriods();

export default function SkillsStatusReport(props) {
  return (
    <ReportContainer>
      <div className="skills-status">
        <DataTable />
      </div>
    </ReportContainer>
  );
}

function DataTable(props) {
  const [data, setData] = useState(null);
  const [studentOrder, setStudentOrder] = useState([]);
  const [sort, setSort] = useState("");
  const [ready, setReady] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState(false);
  const [hydrationError, setHydrationError] = useState(false);
  const [skillModalData, setSkillModalData] = useState(null)

  const domain = useSelector((state) => state.reports.skillsstatus.domain);
  const roster = useSelector((state) => state.reports.roster);
  const locale = useSelector((state) => state.reports.teacher.selectedLocale);
  const selectedCurriculum = useSelector((state) => state?.reports?.teacher?.selectedCurriculum?.value);

  let classId = get(roster, "ui.selections.classroom.value", null);
  if (!classId) classId = get(roster, "ui.selections.classroom.ui.value", null);

  useEffect(() => {
    if (!classId) return;
    let isCancelled = false;

    async function getData() {
      setReady(false);
      setError(false);
      setHydrationError(false);

      let data;
      try {
        data = await SRS.getSkillsStatusData({ classId, domain, locale, selectedCurriculum });
        console.log("skills status data", data);
      } catch (e) {
        console.log(e);
        return setError(true);
      }

      const students = {};
      const order = [];
      for (const s of data.skillsStatus.records) {
        const { studentId } = s;
        students[studentId] = s;
        s.periods = parsePeriods(s);
        order.push(studentId);
      }

      if (isCancelled) return;

      setData(cloneDeep(students));

      const localOrder = JSON.parse(localStorage.getItem(`${classId}_order`));
      if (localOrder) {
        // append new students to beginning of localOrder
        for (const studentId of order)
          if (!localOrder.includes(studentId)) localOrder.unshift(studentId);
        setStudentOrder(localOrder);
      } else setStudentOrder(order);

      // ready to render
      setReady(true);

      let { nextToken } = data.skillsStatus.pageInfo;

      while (nextToken) {
        setHydrating(true);
        let data;
        try {
          data = await SRS.getSkillsStatusData({ classId, domain, nextToken, locale, selectedCurriculum });
        } catch (e) {
          console.log(e);
          setHydrating(false);
          return setHydrationError(true);
        }
        nextToken = data.skillsStatus.pageInfo.nextToken;

        for (const s of data.skillsStatus.records) {
          // previous periods - to be updated
          const { studentId } = s;
          // console.log("students[studentid]", students[studentId]);
          const { periods: currentPeriods } = students[studentId];
          // incoming periods - to be inserted into currentPeriods
          const periods = parsePeriods(s);
          for (const gradeLabel in periods) {
            if (!(gradeLabel in currentPeriods))
              currentPeriods[gradeLabel] = {};
            const skillAreas = currentPeriods[gradeLabel];
            for (const skillName in periods[gradeLabel]) {
              if (!(skillName in currentPeriods[gradeLabel]))
                currentPeriods[gradeLabel][skillName] = [];
              currentPeriods[gradeLabel][skillName].push(
                ...periods[gradeLabel][skillName]
              );
            }
          }
        }
        if (isCancelled) return setHydrating(false);
        setData(cloneDeep(students));
        setHydrating(false);
      }
    }

    getData();

    return () => (isCancelled = true);
  }, [domain, classId, locale, selectedCurriculum]);

  // if API call failed
  if (error) {
    return (
      <div className="filler-text">
        Error: unable to retrieve student records.
      </div>
    );
  }

  // if still waiting for initial data
  if (!ready)
    return (
      <div style={{ height: 500 }}>
        <Loading />
      </div>
    );

  const s = Object.values(data)[0];

  // if students array is empty
  if (!s)
    return (
      <div className="filler-text">
        No student records to display for this class.
      </div>
    );

  // create periodWidths for GradeHeader & column generator
  const myPeriodWidths = {};
  for (const gradeLabel of allPeriods) {
    let width = 0;

    const period = s.periods[gradeLabel];
    if (!period) continue;
    for (const subSkills of Object.values(period)) {
      width += getSkillAreaWidth({ subSkills });
    }
    myPeriodWidths[gradeLabel] = width;
  }

  const activePeriods = Object.keys(myPeriodWidths);
  const headerColor = domainInfo[s.domain].color;

  const renderOrder = Array.from(studentOrder);
  if (sort) {
    renderOrder.sort((id1, id2) => {
      const name1 = data[id1].studentName.toLowerCase();
      const name2 = data[id2].studentName.toLowerCase();
      if (name1 === name2) return 0;
      const comp = name1 > name2 ? 1 : -1;
      if (sort === "alphaAsc") return comp;
      else return comp * -1;
    });
  }

  return (
    <React.Fragment>
      <SkillModal
        data={skillModalData}
        onClose={() => setSkillModalData(null)}
      />
      <DragDropContext
        onDragEnd={onDragEnd}
      >
        <GradeHeader periodWidths={myPeriodWidths} buffer={160} />
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <div className="droppable">
                <div className="table-row">
                  <div className="column is-sticky">
                    <div
                      className="clickable skillname-container table-cell student-name-header"
                      onClick={() => {
                        if (sort === "") setSort("alphaAsc");
                        if (sort === "alphaAsc") setSort("alphaDesc");
                        if (sort === "alphaDesc") setSort("");
                      }}
                    >
                      Student Name
                      {sort === "alphaAsc" && (
                        <FaSortAlphaAsc style={{ marginLeft: 4 }} />
                      )}
                      {sort === "alphaDesc" && (
                        <FaSortAlphaDesc style={{ marginLeft: 4 }} />
                      )}
                    </div>
                  </div>
                  {activePeriods.map((gradeLabel, index) => {
                    const period = s.periods[gradeLabel];
                    return Object.keys(period).map((skillName) => {
                      const subSkills = period[skillName];
                      const width = getSkillAreaWidth({ subSkills });
                      const key = `${gradeLabel}-${skillName}`;
                      return (
                        <div
                          className="column"
                          style={{ width, minWidth: width }}
                          key={key}
                        >
                          <div
                            className="table-cell progression-header skillname-container"
                            style={{
                              background: headerColor,
                              width: width - 4,
                              minWidth: width - 4,
                            }}
                          >
                            <Textfit
                              mode="multi"
                              forceSingleModeWidth={false}
                              max={16}
                            >
                              <div style={{ maxHeight: 50 }}>{skillName}</div>
                            </Textfit>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
              {renderOrder.map((studentId, index) => (
                <DraggableStudentRow
                  student={data[studentId]}
                  sort={sort}
                  index={index}
                  key={studentId}
                  activePeriods={activePeriods}
                  setSkillModalData={setSkillModalData}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {renderHydratingOverlay()}
      {hydrationError && (
        <div className="filler-text">
          Error: some data failed to download for skills status report.
        </div>
      )}
    </React.Fragment>
  );

  function onDragEnd(result) {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    const newOrder = Array.from(studentOrder);
    newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, draggableId);
    setStudentOrder(newOrder);
    localStorage.setItem(`${classId}_order`, JSON.stringify(newOrder));
  }

  function renderHydratingOverlay() {
    if (!hydrating) return;
    return (
      <div
        style={{
          position: "absolute",
          width: 50,
          height: 50,
          top: 20,
          left: 20,
          width: 140,
          height: 72,
          background: "#00000011",
          zIndex: 10,
          padding: 20,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </div>
    );
  }
}

function parsePeriods(s) {
  const periods = {};
  for (const skillArea of s.progression) {
    const { skillName } = skillArea;
    for (const subSkill of skillArea.subSkills) {
      const { grade, period } = subSkill.skillAREAScore;
      const gradeLabel = `${grade}-${period}`;
      if (!(gradeLabel in periods)) periods[gradeLabel] = {};
      if (!(skillName in periods[gradeLabel]))
        periods[gradeLabel][skillName] = [];
      periods[gradeLabel][skillName].push(subSkill);
    }
  }
  return periods;
}
